import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreGameInfo } from '~/stores/strapi/gameInfo';
import type { MetaObject } from 'nuxt/schema';
import type { IAlternates, IMirrorSeoHeadComponent, ISeoHeadComponent } from '~/types/seoHead';
import type { ISsGame } from '~/types/softswiss/ssGames';

export const useHeadGenerate = () => {
  const mirrorDomains = {
    au: 'https://7bitcasino28.com',
    it: 'https://7bitcasino78.com',
    pl: 'https://7bitcasino12.com',
    et: 'https://7bitcasino.com',
    'de-ch': 'https://7bitcasino38.com',
    // Hide this block for now, return after release
    // TODO: add that after release
    // 'en-us': 'https://7bitcasino.one',
    // 'en-us': 'https://7bitcasino12.com',
  };

  const getMirrorCanonical = (mirrorHead: IMirrorSeoHeadComponent) => {
    if (mirrorHead.canonical) {
      if (mirrorHead.canonical.startsWith('http')) {
        return mirrorHead.canonical;
      } else {
        // @ts-ignore
        const domain = mirrorDomains[useStoreRoot().geo];

        return `${domain}${mirrorHead.canonical}`;
      }
    }

    return '';
  };

  const headGenerateAlternates = (metaInfo: MetaObject, alternates?: IAlternates[]): void => {
    if (alternates) {
      alternates.forEach((alter) => {
        if (alter?.lang && alter?.href) {
          // mirror domain special case
          if (alter.lang === 'en-au') {
            metaInfo.link?.push({
              rel: 'alternate',
              hreflang: 'en-au',
              href: alter.href.startsWith('http') ? alter.href : mirrorDomains.au + alter.href,
            });
            return;
          }

          // mirror domain special case
          if (alter.lang === 'de-ch') {
            metaInfo.link?.push({
              rel: 'alternate',
              hreflang: 'de-ch',
              href: alter.href.startsWith('http')
                ? alter.href
                : mirrorDomains['de-ch'] + alter.href,
            });
            return;
          }

          // mirror domain special case
          if (alter.lang === 'it') {
            metaInfo.link?.push({
              rel: 'alternate',
              hreflang: 'it',
              href: alter.href.startsWith('http') ? alter.href : mirrorDomains.it + alter.href,
            });
            return;
          }

          // mirror domain special case
          if (alter.lang === 'pl') {
            metaInfo.link?.push({
              rel: 'alternate',
              hreflang: 'pl',
              href: alter.href.startsWith('http') ? alter.href : mirrorDomains.pl + alter.href,
            });
            return;
          }

          if (alter.lang === 'en') {
            metaInfo.link?.push({
              rel: 'alternate',
              hreflang: 'x-default',
              href: alter.href,
            });
          }

          // // Hide this block for now, return after release
          // // TODO: add that after release
          // // add hreflang for en-us only if local is "en-us" and user is googlebot
          // // in case the useragent is not a googlebot - return anyway to avoid the default statement
          // if (alter.lang === 'en-us') {
          //   const botType = useStoreRoot().botType;

          //   console.log('botType', botType);

          //   // bottype like in nuxt-server but in lowercase
          //   // TODO: return this check after verification and testing
          //   // if (botType === ('google' as TBotType)) {
          //   metaInfo.link?.push({
          //     rel: 'alternate',
          //     hreflang: 'en-us',
          //     href: alter.href.startsWith('http')
          //       ? alter.href
          //       : mirrorDomains['en-us'] + alter.href,
          //   });
          //   // }

          //   return;
          // }

          metaInfo.link?.push({
            rel: 'alternate',
            hreflang: alter.lang,
            href: alter.href,
          });
        }
      });
    }
  };

  const headGenerateCanonical = (metaInfo: MetaObject, canonical?: string): void => {
    if (canonical) {
      metaInfo.link?.push({
        rel: 'canonical',
        href: canonical,
      });
    }
  };

  const headGenerateRobots = (noindex: boolean, nofollow: boolean) => {
    let content = '';

    if (noindex) {
      content = 'noindex';
    }

    if (nofollow) {
      if (content.length) {
        content += ', ';
      }

      content += 'nofollow';
    }

    return content.length ? { name: 'robots', content } : null;
  };

  const headGenerate = (
    bodyClass: string,
    head: ISeoHeadComponent | undefined,
    cataloguePageData?: any | undefined
  ) => {
    const metaInfo: MetaObject = {
      htmlAttrs: {
        lang: useStoreRoot().userLang,
      },
      title: '',
      meta: [
        {
          hid: 'og:url',
          name: 'og:url',
          property: 'og:url',
          content: (import.meta.browser && window && window.location.href) || '',
        },
        {
          hid: 'og:locale',
          name: 'og:locale',
          property: 'og:locale',
          content: useStoreRoot().userLang,
        },
      ],
      link: [],
    };
    if (bodyClass) {
      metaInfo.bodyAttrs = {
        class: bodyClass,
      };
    }

    if (head) {
      const robots = headGenerateRobots(head.noindex, head.nofollow);
      if (robots) {
        metaInfo.meta?.push(robots);
      }

      if (head.title) {
        metaInfo.title = head.title;

        metaInfo.meta?.push({
          hid: 'og:title',
          name: 'og:title',
          content: head.title,
        });

        metaInfo.meta?.push({
          hid: 'og:site_name',
          name: 'og:site_name',
          content: head.title,
        });
      } else if (cataloguePageData) {
        metaInfo.title = cataloguePageData.defaultTitle;
      }

      if (head.description) {
        metaInfo.meta?.push({
          hid: 'description',
          name: 'description',
          content: head.description,
        });

        metaInfo.meta?.push({
          hid: 'og:description',
          name: 'og:description',
          content: head.description,
        });
      } else if (cataloguePageData) {
        metaInfo.meta?.push({
          hid: 'description',
          name: 'description',
          content: cataloguePageData.defaultMetaDescription,
        });
      }

      headGenerateCanonical(metaInfo, head.canonical || undefined);
      headGenerateAlternates(metaInfo, head.alternates || undefined);
    }

    return metaInfo;
  };

  const headGenerateGamePage = (game: ISsGame | null, bodyClass: string) => {
    const metaInfo: MetaObject = {
      htmlAttrs: {
        lang: useStoreRoot().userLang,
      },
      bodyAttrs: {
        class: bodyClass,
      },
      title: game?.title || '',
      meta: [],
      link: [],
    };

    if (useStoreGameInfo().gameInfo?.head) {
      const head = useStoreGameInfo().gameInfo.head;

      if (head.title) {
        const titleMetaInfo = head.title
          .replace(/%game%/i, game?.title || '')
          .replace(/%provider%/i, game?.provider || '');
        metaInfo.title = titleMetaInfo;

        metaInfo.meta?.push({
          hid: 'og:title',
          name: 'og:title',
          content: titleMetaInfo,
        });
      }

      if (head.description) {
        const contentDescription = head.description
          .replace(/%game%/i, game?.title || '')
          .replace(/%provider%/i, game?.provider || '');

        metaInfo.meta?.push({
          hid: 'description',
          name: 'description',
          content: contentDescription,
        });

        metaInfo.meta?.push({
          hid: 'og:description',
          name: 'og:description',
          content: contentDescription,
        });
      }

      headGenerateCanonical(metaInfo, head.canonical as string);
      headGenerateAlternates(metaInfo, head.alternates);
    }

    return metaInfo;
  };

  return {
    getMirrorCanonical,
    headGenerateAlternates,
    headGenerateCanonical,
    headGenerateRobots,
    headGenerate,
    headGenerateGamePage,
  };
};
